import { useCallback, useEffect, useRef, useState } from "react";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import {
  hasTherapyInputErrors,
  toConditionInputs,
  toTherapyInputs,
} from "pages/MasterPengobatanStandar/validation";

const AUTOSAVE_MS = 800;

// Muat satu Rule (GET /standard-treatment-rule/{id}) dan autosave perubahan
// Kondisi/Terapinya lewat PUT replace-all. saveStatus: saved | pending | saving | error.
const useRuleEditor = (ruleId, { readOnly = false } = {}) => {
  const [rule, setRule] = useState(null);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [dirty, setDirty] = useState({ conditions: false, therapies: false });
  const [saveStatus, setSaveStatus] = useState("saved");
  const [saveErrors, setSaveErrors] = useState([]);
  const current = useRef(ruleId);
  const editSeq = useRef(0); // naik setiap edit; cegah edit baru ikut ditandai tersimpan

  const load = useCallback(() => {
    if (!ruleId) return Promise.resolve();
    current.current = ruleId;
    setLoadState({ loading: true, error: null });
    return standardTreatmentService
      .getRule(ruleId)
      .then((data) => {
        if (current.current !== ruleId) return;
        setRule(data);
        setDirty({ conditions: false, therapies: false });
        setSaveStatus("saved");
        setSaveErrors([]);
        setLoadState({ loading: false, error: null });
      })
      .catch((err) => current.current === ruleId && setLoadState({ loading: false, error: errorMessages(err) }));
  }, [ruleId]);

  useEffect(() => {
    load();
  }, [load]);

  const setConditions = (conditions) => {
    editSeq.current += 1;
    setRule((prev) => ({ ...prev, rule_conditions: conditions }));
    setDirty((prev) => ({ ...prev, conditions: true }));
  };
  const setTherapies = (therapies) => {
    editSeq.current += 1;
    setRule((prev) => ({ ...prev, rule_therapies: therapies }));
    setDirty((prev) => ({ ...prev, therapies: true }));
  };

  // Autosave berdebounce; terapi yang belum valid tidak dikirim (pasti 400).
  useEffect(() => {
    if (readOnly || !rule || (!dirty.conditions && !dirty.therapies)) return undefined;
    const therapiesBlocked = dirty.therapies && hasTherapyInputErrors(rule.rule_therapies);
    setSaveStatus(therapiesBlocked && !dirty.conditions ? "pending" : "saving");
    const timer = setTimeout(() => {
      const seq = editSeq.current;
      const calls = [];
      if (dirty.conditions)
        calls.push(standardTreatmentService.replaceConditions(rule.id, toConditionInputs(rule.rule_conditions)).then(() => "conditions"));
      if (dirty.therapies && !therapiesBlocked)
        calls.push(standardTreatmentService.replaceTherapies(rule.id, toTherapyInputs(rule.rule_therapies)).then(() => "therapies"));
      Promise.all(calls)
        .then((saved) => {
          if (seq !== editSeq.current) return; // ada edit baru; timer berikutnya yang menyimpan
          setDirty((prev) => ({
            conditions: saved.includes("conditions") ? false : prev.conditions,
            therapies: saved.includes("therapies") ? false : prev.therapies,
          }));
          setSaveErrors([]);
          setSaveStatus(therapiesBlocked ? "pending" : "saved");
        })
        .catch((err) => {
          setSaveErrors(errorMessages(err));
          setSaveStatus("error");
        });
    }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [rule, dirty, readOnly]);

  return { rule, ...loadState, reload: load, setConditions, setTherapies, saveStatus, saveErrors };
};

export default useRuleEditor;
